import React, { useState } from 'react';
import styled from 'styled-components';
import { MenuProps as AntdMenuProps } from 'antd/lib/menu';
import { Menu, MenuItem, Icon, Dropdown, Text, theme } from 'components';
import { Template } from 'api/TemplateController';

export type TemplateDropdownType = (Omit<
  Template,
  '_id' | 'createdAt' | 'updatedAt' | 'org'
> & {
  id: string;
  _id?: string;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  org?: string;
})[];

export interface TemplateDropdownProps {
  templates: TemplateDropdownType;
  value?: string;
  onChange: (id: string) => void;
  disabled?: boolean;
}

const TemplateDropdown = (props: TemplateDropdownProps) => {
  const { templates, value, onChange, disabled } = props;

  const [open, setOpen] = useState<boolean>(false);

  const onSelect: AntdMenuProps['onClick'] = ({ key }) => {
    onChange(templates[Number(key)].id);
    setOpen(false);
  };

  const getDefaultLabel = (template: Pick<Template, 'isDefault' | 'role'>) => {
    if (!template.isDefault) return '';
    switch (template.role) {
      case 'BRANCH':
        return 'Branch Default';
      case 'TEAM':
        return 'Team Default';
      case 'GLOBAL':
        return 'Global Default';
      default:
        return 'Default';
    }
  };

  return (
    <Dropdown
      trigger={['click']}
      open={open}
      onOpenChange={visible => setOpen(visible)}
      dropdownRender={() => (
        <StyledMenu onClick={onSelect}>
          {templates.map((template: any, idx: number) => {
            const { name, id } = template;
            const iconWidth = 18;
            return (
              <MenuItem
                key={idx}
                icon={
                  <StyledIconWrapper $width={iconWidth}>
                    {id === value && (
                      <Icon type="check" color="primary" width={iconWidth} />
                    )}
                  </StyledIconWrapper>
                }
                data-cy={`template-menu-${name}`}
              >
                <StyledTemplateNameWrapper>
                  <Text>{name}</Text>
                </StyledTemplateNameWrapper>
                <div>
                  <Text type="secondary">{getDefaultLabel(template)}</Text>
                </div>
              </MenuItem>
            );
          })}
        </StyledMenu>
      )}
      disabled={disabled}
    >
      <TemplateDropdownButton data-cy="template-dropdown-button">
        <TemplateCurrentValueLabel
          disabled={disabled}
          data-cy="template-dropdown-label"
        >
          {templates.find(t => t.id === value)?.name || '-'}
        </TemplateCurrentValueLabel>
        <Icon flipped={open} type="arrowDownThick" width={8} />
      </TemplateDropdownButton>
    </Dropdown>
  );
};

export default TemplateDropdown;

// TODO: Make this a button for consistent hover behaviour
const TemplateDropdownButton = styled.div`
  background: ${theme.gray3};
  border-radius: 4px;
  font-size: 13px;
  line-height: 24px;
  min-width: 3em;
  padding: 3px 12px;

  display: flex;
  flex-flow: row nowrap;
  align-items: baseline;
`;

const TemplateCurrentValueLabel = styled.span<{ disabled?: boolean }>`
  flex: 1;
  ${({ disabled }) => (disabled ? 'color: rgba(0, 0, 0, 0.25);' : '')}
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledIconWrapper = styled.div<{ $width: number }>`
  width: ${({ $width }) => $width}px;
`;

const StyledTemplateNameWrapper = styled.div`
  max-width: 500px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledMenu = styled(Menu)`
  max-height: 35vh;

  &::-webkit-scrollbar {
    background: #fff;
    -webkit-appearance: none;
  }
  &::-webkit-scrollbar:vertical {
    width: 11px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 2px solid #fff; /* should match background */
    background-color: rgba(0, 0, 0, 0.4);
  }
`;
